/* ============================================================================
   tpw-roll.js — shared digit-roll / odometer (F-07)
   ----------------------------------------------------------------------------
   Extracted + generalised from the Genius Bar reel (assets/tpw-genius-bar.js
   _rollReels) so every surface (header cart count + total now; PDP / proof-band
   stats later) can reuse ONE roll instead of re-implementing the effect.

   Public API (window.TPWRoll):
     roll(el, newText, opts?)   animate el's text to newText, digits on reels
     set(el, text)              set text instantly (no animation), reel-shaped
     reduced()                  -> boolean, current prefers-reduced-motion state

   Design idiom (matches the Genius Bar exactly):
     · each digit run becomes vertical reels (overflow:hidden), one cell per digit
     · the reel slides up its sequence with cubic-bezier(.16,1,.3,1)
     · per-column stagger so the number "settles" left-to-right
     · non-digit glyphs ($ , . + etc.) render as static lit spans
   Native-feel sizing:
     · reels inherit the host's font / colour / weight (it's just spans inside el)
     · reel HEIGHT is measured from the host's own line-box (1em line) so digits
       sit on the same baseline as the surrounding text — drop-in, zero CLS:
       the element's box never changes size, only the inner cells translate.

   Accessibility:
     · prefers-reduced-motion -> instant swap, no roll (live, honoured per-call)
     · the reels are presentational; callers keep their own .visually-hidden text
       (the cart bubble + total each already carry a sr-only mirror), so screen
       readers are unaffected — we only touch the aria-hidden visual numerals.

   Correctness-over-flourish (TPW craft bar):
     · the FINAL value is committed to the DOM up front (the reel's last cell is
       the true target digit); the animation only decorates the transition, so a
       dropped frame / interrupted roll can never leave a stale or wrong number.
   ============================================================================ */
(function () {
  'use strict';
  if (window.TPWRoll) return;

  var DUR = 0.74;                 // seconds — matches Genius Bar reel
  var EASE = 'cubic-bezier(.16,1,.3,1)';
  var STAGGER = 45;               // ms per column (right-to-left settle)
  var STYLE_ID = 'tpw-roll-style';

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }

  // Compliance 2 (GATE-01-02 4.3, reg 20(2)(e)): a tobacco price never animates, so no tween frame or reel ever shows a
  // figure that is not the price. Still (instant, exact) when the page is a tobacco surface (html.tps-band), when the
  // drawer holds tobacco (.tps-drawer--tobacco), or when the server marked the node (data-tps-still, header total).
  function still(el) {
    return reduced() || document.documentElement.classList.contains('tps-band') ||
      !!(el && el.closest && el.closest('[data-tps-still], .tps-drawer--tobacco'));
  }

  function injectCSS() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    // Reels are inline so they flow with the host text. Height/line-height are
    // set inline per-element (measured from the host) to keep the baseline.
    s.textContent =
      '.tpw-roll{font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1}' +
      // R1 O10 (Tiresias R1T-19, 1.4.12): the reel is a fixed-px window measured from the host's line-height; under a
      // user text-spacing override (line-height 1.5) the resting digit grew past it and lost its lower half. At rest
      // the reel holds ONE cell, so it only needs to clip while a roll is in flight.
      '.tpw-roll__reel{display:inline-block;overflow:visible;vertical-align:top;text-align:center}' +
      '.tpw-roll__reel.is-rolling{overflow:hidden}' +
      // resting transform is translateY(0) => cell 0 (= the TARGET) shows. The
      // spin is a CSS animation (auto-completes, no JS timer dependency); when it
      // ends the element returns to this resting state = target. So a starved
      // JS environment can never leave a stale digit.
      '.tpw-roll__inner{display:block;will-change:transform;transform:translateY(0)}' +
      '.tpw-roll__cell{display:block}' +
      '.tpw-roll__lit{display:inline-block;vertical-align:top}';
    document.head.appendChild(s);
  }

  // Measure the host's single-line box height in px so reels match the baseline.
  function lineHeightPx(el) {
    var cs = window.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.2;
    return Math.round(lh);
  }

  // Build the static (instant) reel markup for `text` — last cell = the target.
  function staticHTML(text) {
    var out = '';
    for (var i = 0; i < text.length; i++) {
      var c = text[i];
      if (c >= '0' && c <= '9') {
        out += '<span class="tpw-roll__reel" data-d="' + c + '">' +
                 '<span class="tpw-roll__inner"><span class="tpw-roll__cell">' + c + '</span></span>' +
               '</span>';
      } else {
        out += '<span class="tpw-roll__lit">' + esc(c) + '</span>';
      }
    }
    return out;
  }

  function esc(s) {
    if (s === ' ') return '&nbsp;';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Prepare el as a roll host: ensure class + measured reel sizing baked in.
  function prep(el, h) {
    el.classList.add('tpw-roll');
    el.setAttribute('data-tpw-roll-h', h);
  }

  // Instant set — no animation. Reel-shaped so a later roll() is seamless.
  function set(el, text) {
    if (text == null) return;
    text = String(text);
    injectCSS();
    var h = lineHeightPx(el);
    prep(el, h);
    el.innerHTML = staticHTML(text);
    sizeReels(el, h);
    el.setAttribute('data-tpw-roll-text', text);
  }

  function sizeReels(el, h) {
    var reels = el.querySelectorAll('.tpw-roll__reel');
    for (var i = 0; i < reels.length; i++) {
      reels[i].style.height = h + 'px';
      reels[i].style.lineHeight = h + 'px';
      var cells = reels[i].querySelectorAll('.tpw-roll__cell');
      for (var j = 0; j < cells.length; j++) { cells[j].style.height = h + 'px'; cells[j].style.lineHeight = h + 'px'; }
    }
  }

  // Animate el -> newText. Digits in matching positions (counted from the RIGHT,
  // so $1,204.80 -> $1,250.00 rolls only the changed columns visually but every
  // reel ends on its true target) spin from old to new; new/extra digits spin in.
  function roll(el, newText, opts) {
    if (newText == null) return;
    newText = String(newText);
    opts = opts || {};
    injectCSS();

    var h = lineHeightPx(el);
    // Previous value: prefer an explicit opts.from (the caller's own cache) since
    // the host element is frequently REPLACED wholesale (e.g. the cart bubble is
    // re-rendered on every update, so a per-element data-attr can't survive).
    // Fall back to the element's own attribute for in-place callers.
    var prevText = (opts.from != null) ? String(opts.from) : el.getAttribute('data-tpw-roll-text');

    // Reduced motion OR no previous value OR unchanged -> instant.
    if (still(el) || prevText == null || prevText === newText) {
      set(el, newText);
      return;
    }

    prep(el, h);

    // Map old digits onto new digit columns by right-alignment so a reel that
    // didn't change visually still "lands" on the right number.
    var oldDigits = (prevText.match(/\d/g) || []);
    var newDigits = (newText.match(/\d/g) || []);
    var offset = newDigits.length - oldDigits.length; // >0 means digits added

    el.innerHTML = staticHTML(newText);
    sizeReels(el, h);

    var reels = el.querySelectorAll('.tpw-roll__reel');
    var nCols = reels.length;

    [].forEach.call(reels, function (reel, col) {
      var target = parseInt(reel.getAttribute('data-d'), 10);
      var oldIx = col - offset;                 // matching old digit index
      var from = (oldIx >= 0 && oldIx < oldDigits.length) ? parseInt(oldDigits[oldIx], 10) : null;
      var inner = reel.querySelector('.tpw-roll__inner');

      // Cells laid out TARGET FIRST (cell 0) then the spin path down to `from`
      // (last cell):  [target, target-1, ... , from]
      // Resting transform translateY(0) shows cell 0 = TARGET. The animation
      // STARTS scrolled down (showing `from`, the bottom cell) and runs UP to
      // translateY(0) (target). When the CSS animation ends it returns to the
      // resting style = target. So even if the JS that started it is the LAST JS
      // to run, the committed/visible value is always the target.
      var seq = [];
      if (from === null) {
        var steps = 8;
        for (var k = 0; k < steps; k++) seq.push((target - k + 100) % 10); // target,..,target-7
      } else {
        var dist = (target - from + 10) % 10;          // forward turns from->target
        if (dist === 0) { set1(reel, h, target); return; } // unchanged: show target
        for (var d = 0; d <= dist; d++) seq.push((target - d + 10) % 10);  // target..from
      }

      inner.innerHTML = seq.map(function (n) {
        return '<span class="tpw-roll__cell" style="height:' + h + 'px;line-height:' + h + 'px">' + n + '</span>';
      }).join('');

      var startPx = (seq.length - 1) * h;                // showing `from` (bottom cell)
      var delay = (nCols - 1 - col) * STAGGER;           // settle right-to-left

      // CSS keyframe animation: from translateY(-startPx) (=from) to 0 (=target).
      // animation has no fill-mode -> when it ends the inner returns to its base
      // style (translateY(0) = target). Self-completing, no JS timer needed.
      var name = mkKeyframes(startPx);
      reel.classList.add('is-rolling');
      inner.style.animation = 'none';
      void inner.offsetHeight;                            // reseat so re-runs restart
      // FILL = 'forwards' (NOT 'both'), founder 2026-06-26. 'both' applied the
      // BACKWARDS fill = the start frame (translateY(-startPx) = the spin sequence)
      // during the delay AND whenever the animation can't tick — on a HIDDEN host
      // (closed cart drawer) or a host re-rendered mid-animation the reel froze at
      // that start frame, rendering garbage like "$1098765...". With 'forwards' a
      // non-running animation shows the inner's BASE style (translateY(0) = TARGET),
      // so the correct value ALWAYS shows; it still animates start->target when the
      // host is visible. Roll = a transient enhancement; correctness is guaranteed.
      inner.style.animation = name + ' ' + DUR + 's ' + EASE + ' ' + (delay / 1000) + 's forwards';

      // P-018 ghost/double-digit-at-rest fix (2026-06-30). At rest the inner keeps
      // the WHOLE spin sequence ([target, target-1, ... , from]) and relies only on
      // the reel's overflow:hidden + translateY(0) to clip the neighbour cells. But
      // when the host's line-height is tight (e.g. .header-cart__total{line-height:1}
      // -> a 14px clip window for a 14px DM Mono glyph whose natural line is ~1.25em),
      // the cell BELOW the target (target-1) bleeds its top edge into the bottom of
      // the clip window = a faint doubled/ghosted trailing digit. Collapse the inner
      // back to the SINGLE target cell once the roll settles so there is no neighbour
      // left to bleed, independent of the host's line-height. Self-healing: if the
      // animationend never fires (hidden host etc.) the 'forwards' fill already shows
      // the target, and the next set()/roll() rebuilds the inner from scratch anyway.
      collapseOnEnd(reel, inner, h, target);
    });

    el.setAttribute('data-tpw-roll-text', newText);
  }

  // Create (once per distance) a keyframe that slides up from -startPx to 0.
  var _kf = {};
  function mkKeyframes(startPx) {
    var key = 'k' + startPx;
    if (_kf[key]) return _kf[key];
    var name = 'tpwRoll' + startPx;
    var sheet = document.getElementById(STYLE_ID).sheet;
    try {
      sheet.insertRule(
        '@keyframes ' + name + '{from{transform:translateY(-' + startPx + 'px)}to{transform:translateY(0)}}',
        sheet.cssRules.length
      );
    } catch (e) { /* some browsers throw on dynamic @keyframes via insertRule; fall back */ }
    _kf[key] = name;
    return name;
  }

  // show a reel's single target digit instantly (unchanged column)
  function set1(reel, h, digit) {
    var inner = reel.querySelector('.tpw-roll__inner');
    reel.classList.remove('is-rolling');
    inner.style.animation = 'none';
    inner.innerHTML = '<span class="tpw-roll__cell" style="height:' + h + 'px;line-height:' + h + 'px">' + digit + '</span>';
    inner.style.transform = 'translateY(0)';
  }

  // P-018: once the spin animation finishes, collapse the multi-cell inner back to
  // the single target cell so no neighbour cell can bleed through the clip window at
  // rest (see the long note at the roll() call site). Guarded so it fires exactly
  // once and is harmless if the inner was already rebuilt by a newer roll/set.
  function collapseOnEnd(reel, inner, h, target) {
    if (!inner || typeof inner.addEventListener !== 'function') return;
    function done() {
      inner.removeEventListener('animationend', done);
      reel.classList.remove('is-rolling');
      // Only collapse if THIS inner still holds the spin sequence (>1 cell). A newer
      // set()/roll() may have replaced it; in that case leave the fresh value alone.
      if (inner.querySelectorAll('.tpw-roll__cell').length > 1) {
        inner.style.animation = 'none';
        inner.innerHTML = '<span class="tpw-roll__cell" style="height:' + h + 'px;line-height:' + h + 'px">' + target + '</span>';
        inner.style.transform = 'translateY(0)';
      }
    }
    inner.addEventListener('animationend', done);
  }

  /* ==========================================================================
     tween() — Option B "count tween" (founder 2026-06-27)
     --------------------------------------------------------------------------
     A SMOOTH count from the old value to the new through REAL intermediate
     values (not a per-digit slot-machine spin). Used where the odometer reads
     "busy" — the cart drawer line price + subtotal on a qty change. The header
     count/total keep the reel roll (small/stable digits look clean there).

     Reliability (same craft bar as roll): the exact target is committed the
     instant the tween can't run (reduced-motion, no prior value, or a starved
     timer) — `data-tpw-roll-text` is set to the target up front and the loop is
     setTimeout-driven (NOT rAF — rAF is starved on this theme in bg tabs / when
     the footer map blocks idle), so the number always lands on the true value.
     Plain text only (no reels) -> immune to the reel-garbage class of bugs.
     ========================================================================== */
  function parseNum(s) {
    var v = parseFloat(String(s).replace(/[^\d.\-]/g, ''));
    return isNaN(v) ? null : v;
  }
  // Re-format `value` using the punctuation of `template` (currency symbol,
  // thousands comma, decimal places) so each frame reads like a real price.
  function fmtLike(template, value) {
    template = String(template);
    var decs = 0, dot = template.lastIndexOf('.');
    if (dot > -1) decs = (template.slice(dot + 1).match(/\d/g) || []).length;
    var neg = value < 0; value = Math.abs(value);
    var fixed = value.toFixed(decs), ip = fixed, dp = '';
    if (decs) { var parts = fixed.split('.'); ip = parts[0]; dp = parts[1]; }
    if (template.indexOf(',') > -1) ip = ip.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var prefix = (template.match(/^[^\d.\-]*/) || [''])[0];
    return prefix + (neg ? '-' : '') + ip + (decs ? '.' + dp : '');
  }
  function tween(el, fromStr, toStr, opts) {
    if (!el || toStr == null) return;
    toStr = String(toStr); opts = opts || {};
    el.setAttribute('data-tpw-roll-text', toStr);     // commit the true target up front
    var to = parseNum(toStr), from = parseNum(fromStr);
    // reduced-motion / no usable prior value / unchanged -> instant, exact.
    if (still(el) || from == null || to == null || from === to) { el.textContent = toStr; return; }
    el.classList.add('tpw-roll');                     // tabular-nums so widths don't jitter
    var dur = opts.dur || 360, startT = Date.now();
    el.textContent = fmtLike(toStr, from);            // seamless: start from the old value
    function step() {
      var p = (Date.now() - startT) / dur;
      if (p >= 1) { el.textContent = toStr; return; } // land exactly on the target string
      var e = 1 - Math.pow(1 - p, 3);                 // ease-out cubic = the "smooth settle"
      el.textContent = fmtLike(toStr, from + (to - from) * e);
      setTimeout(step, 16);
    }
    setTimeout(step, 16);
  }

  window.TPWRoll = { roll: roll, set: set, tween: tween, reduced: reduced };
})();
